import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Users, Plus, Search, Building2, Phone, Mail, MapPin, Calendar,
  Edit, Eye, Trash2, Filter, FileText, DollarSign, AlertCircle,
  CheckCircle, XCircle, ArrowRight, User, Briefcase
} from 'lucide-react';
import TenantForm from '../forms/TenantForm';

const Tenants = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);

  const typeOptions = ['all', 'company', 'individual'];

  // Mock tenants data
  const [tenantsList, setTenantsList] = useState([
    {
      id: 'TEN-001',
      name: 'ABC Retail Company',
      type: 'company',
      registrationNumber: 'CR-1234567890',
      contactPerson: 'Ahmed Al-Rashid',
      phone: '+966 50 123 4567',
      email: 'contact@abcretail.com',
      address: 'King Fahd Road, Riyadh',
      city: 'Riyadh',
      status: 'active',
      propertiesRented: 1,
      totalMonthlyRent: 15000,
      totalPaid: 270000,
      totalPending: 0,
      contractsCount: 1,
      activeContracts: 1,
      expiredContracts: 0,
      paymentHistory: 'excellent',
      issuesCount: 0,
      joinDate: '2023-01-15',
      lastPaymentDate: '2024-01-15',
      notes: 'Long-term tenant, excellent payment history'
    },
    {
      id: 'TEN-002',
      name: 'XYZ Logistics',
      type: 'company',
      registrationNumber: 'CR-9876543210',
      contactPerson: 'Mohammed Ali',
      phone: '+966 50 987 6543',
      email: 'info@xyzlogistics.com',
      address: 'Industrial Area, Jeddah',
      city: 'Jeddah',
      status: 'active',
      propertiesRented: 1,
      totalMonthlyRent: 25000,
      totalPaid: 475000,
      totalPending: 25000,
      contractsCount: 1,
      activeContracts: 1,
      expiredContracts: 0,
      paymentHistory: 'good',
      issuesCount: 2,
      joinDate: '2022-06-01',
      lastPaymentDate: '2024-01-01',
      notes: 'Payment overdue, follow up required'
    },
    {
      id: 'TEN-003',
      name: 'Tech Solutions Inc.',
      type: 'company',
      registrationNumber: 'CR-5555555555',
      contactPerson: 'Omar Hassan',
      phone: '+966 50 555 1234',
      email: 'admin@techsolutions.com',
      address: 'Corniche Road, Dammam',
      city: 'Dammam',
      status: 'active',
      propertiesRented: 1,
      totalMonthlyRent: 18000,
      totalPaid: 198000,
      totalPending: 18000,
      contractsCount: 1,
      activeContracts: 1,
      expiredContracts: 0,
      paymentHistory: 'good',
      issuesCount: 1,
      joinDate: '2023-03-01',
      lastPaymentDate: '2024-01-10',
      notes: 'Payment overdue, send reminder'
    },
    {
      id: 'TEN-004',
      name: 'Storage Co.',
      type: 'company',
      registrationNumber: 'CR-4444444444',
      contactPerson: 'Khalid Omar',
      phone: '+966 50 444 5678',
      email: 'contact@storageco.com',
      address: 'Industrial Zone, Khobar',
      city: 'Khobar',
      status: 'active',
      propertiesRented: 1,
      totalMonthlyRent: 20000,
      totalPaid: 960000,
      totalPending: 0,
      contractsCount: 1,
      activeContracts: 1,
      expiredContracts: 0,
      paymentHistory: 'excellent',
      issuesCount: 3,
      joinDate: '2021-01-01',
      lastPaymentDate: '2024-01-15',
      notes: 'Contract expiring soon, discuss renewal'
    },
    {
      id: 'TEN-005',
      name: 'Mohammed Al-Saud',
      type: 'individual',
      nationalId: '1234567890',
      contactPerson: 'Mohammed Al-Saud',
      phone: '+966 50 111 2222',
      email: 'm.alsaud@email.com',
      address: 'Olaya Street, Riyadh',
      city: 'Riyadh',
      status: 'inactive',
      propertiesRented: 0,
      totalMonthlyRent: 0,
      totalPaid: 0,
      totalPending: 0,
      contractsCount: 0,
      activeContracts: 0,
      expiredContracts: 1,
      paymentHistory: 'good',
      issuesCount: 0,
      joinDate: '2020-01-01',
      lastPaymentDate: '2023-12-31',
      notes: 'Previous tenant, contract expired'
    },
  ]);

  const handleSaveTenant = (tenantData) => {
    if (editingTenant) {
      setTenantsList(prev => prev.map(t => 
        t.id === editingTenant.id ? { ...t, ...tenantData, id: editingTenant.id } : t
      ));
      setEditingTenant(null);
    } else {
      const newTenant = {
        ...tenantData,
        id: `TEN-${String(tenantsList.length + 1).padStart(3, '0')}`,
        status: 'active',
        propertiesRented: 0,
        totalMonthlyRent: 0,
        totalPaid: 0,
        totalPending: 0,
        contractsCount: 0,
        activeContracts: 0,
        expiredContracts: 0,
        paymentHistory: 'good',
        issuesCount: 0,
        joinDate: new Date().toISOString().split('T')[0],
        lastPaymentDate: null,
      };
      setTenantsList(prev => [...prev, newTenant]);
      setShowCreateModal(false);
    }
  };

  const handleDeleteTenant = (tenantId) => {
    if (window.confirm('Are you sure you want to delete this tenant?')) {
      setTenantsList(prev => prev.filter(t => t.id !== tenantId));
      if (selectedTenant?.id === tenantId) {
        setSelectedTenant(null);
      }
    }
  };

  const filteredTenants = tenantsList.filter(tenant => {
    const matchesSearch = tenant.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tenant.phone.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tenant.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (tenant.registrationNumber && tenant.registrationNumber.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (tenant.nationalId && tenant.nationalId.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesFilter = selectedFilter === 'all' || tenant.type === selectedFilter;
    
    return matchesSearch && matchesFilter;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-700';
      case 'inactive': return 'bg-gray-100 text-gray-700';
      case 'suspended': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getPaymentHistoryColor = (history) => {
    switch (history) {
      case 'excellent': return 'text-green-600';
      case 'good': return 'text-blue-600';
      case 'fair': return 'text-orange-600';
      case 'poor': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const TenantCard = ({ tenant }) => {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-lg transition-all cursor-pointer"
        onClick={() => setSelectedTenant(tenant)}
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-start gap-4 flex-1">
            <div className={`p-3 rounded-lg ${tenant.type === 'company' ? 'bg-blue-100' : 'bg-purple-100'}`}>
              {tenant.type === 'company' ? (
                <Building2 className={`w-6 h-6 text-blue-600`} />
              ) : (
                <User className={`w-6 h-6 text-purple-600`} />
              )}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-lg font-semibold text-gray-900">{tenant.name}</h3>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(tenant.status)}`}>
                  {tenant.status}
                </span>
              </div>
              <p className="text-sm text-gray-600 mb-2 capitalize">{tenant.type}</p>
              {tenant.type === 'company' && tenant.registrationNumber && (
                <p className="text-sm text-gray-600 mb-2">CR: {tenant.registrationNumber}</p>
              )}
              {tenant.type === 'individual' && tenant.nationalId && (
                <p className="text-sm text-gray-600 mb-2">ID: {tenant.nationalId}</p>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-2 border-t border-gray-200 pt-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Contact Person</span>
            <span className="text-sm font-medium text-gray-900">{tenant.contactPerson}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Phone</span>
            <span className="text-sm text-gray-900">{tenant.phone}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Email</span>
            <span className="text-sm text-gray-900">{tenant.email}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Location</span>
            <span className="text-sm text-gray-900">{tenant.city}</span>
          </div>
          {tenant.status === 'active' && (
            <>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Properties Rented</span>
                <span className="text-sm font-semibold text-gray-900">{tenant.propertiesRented}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Monthly Rent</span>
                <span className="text-sm font-semibold text-gray-900">SAR {tenant.totalMonthlyRent.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Payment History</span>
                <span className={`text-sm font-medium capitalize ${getPaymentHistoryColor(tenant.paymentHistory)}`}>
                  {tenant.paymentHistory}
                </span>
              </div>
            </>
          )}
        </div>

        {tenant.totalPending > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center gap-2 text-red-600">
              <AlertCircle className="w-4 h-4" />
              <span className="text-sm font-medium">SAR {tenant.totalPending.toLocaleString()} pending</span>
            </div>
          </div>
        )}

        {tenant.issuesCount > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center gap-2 text-orange-600">
              <AlertCircle className="w-4 h-4" />
              <span className="text-sm font-medium">{tenant.issuesCount} active issue{tenant.issuesCount > 1 ? 's' : ''}</span>
            </div>
          </div>
        )}
      </motion.div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header Actions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Tenants</h2>
            <p className="text-sm text-gray-600 mt-1">
              Manage companies and individuals renting properties
            </p>
          </div>
          <div className="flex gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search tenants..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 flex items-center gap-2 text-sm font-medium"
            >
              <Plus className="w-4 h-4" />
              New Tenant
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="mt-4 flex flex-wrap gap-2">
          {typeOptions.map((type) => (
            <button
              key={type}
              onClick={() => setSelectedFilter(type)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedFilter === type
                  ? 'bg-amber-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Tenants</p>
              <p className="text-2xl font-bold text-gray-900">{tenantsList.length}</p>
            </div>
            <Users className="w-8 h-8 text-amber-600" />
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active Tenants</p>
              <p className="text-2xl font-bold text-green-600">
                {tenantsList.filter(t => t.status === 'active').length}
              </p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Companies</p>
              <p className="text-2xl font-bold text-blue-600">
                {tenantsList.filter(t => t.type === 'company').length}
              </p>
            </div>
            <Building2 className="w-8 h-8 text-blue-600" />
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pending Payments</p>
              <p className="text-2xl font-bold text-red-600">
                SAR {tenantsList.reduce((sum, t) => sum + t.totalPending, 0).toLocaleString()}
              </p>
            </div>
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
        </div>
      </div>

      {/* Tenants Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredTenants.map((tenant) => (
          <TenantCard key={tenant.id} tenant={tenant} />
        ))}
      </div>

      {/* Tenant Detail Modal */}
      {selectedTenant && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900">Tenant Details</h3>
              <button
                onClick={() => setSelectedTenant(null)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <XCircle className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              {/* Tenant Info */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="text-sm font-medium text-gray-700">Name</label>
                  <p className="text-lg text-gray-900">{selectedTenant.name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Type</label>
                  <p className="text-lg text-gray-900 capitalize">{selectedTenant.type}</p>
                </div>
                {selectedTenant.type === 'company' && selectedTenant.registrationNumber && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">Registration Number</label>
                    <p className="text-lg text-gray-900">{selectedTenant.registrationNumber}</p>
                  </div>
                )}
                {selectedTenant.type === 'individual' && selectedTenant.nationalId && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">National ID</label>
                    <p className="text-lg text-gray-900">{selectedTenant.nationalId}</p>
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium text-gray-700">Contact Person</label>
                  <p className="text-lg text-gray-900">{selectedTenant.contactPerson}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Status</label>
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedTenant.status)}`}>
                    {selectedTenant.status}
                  </span>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Phone</label>
                  <p className="text-lg text-gray-900">{selectedTenant.phone}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Email</label>
                  <p className="text-lg text-gray-900">{selectedTenant.email}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Address</label>
                  <p className="text-lg text-gray-900">{selectedTenant.address}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">City</label>
                  <p className="text-lg text-gray-900">{selectedTenant.city}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Join Date</label>
                  <p className="text-lg text-gray-900">{selectedTenant.joinDate}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Last Payment</label>
                  <p className="text-lg text-gray-900">{selectedTenant.lastPaymentDate || 'N/A'}</p>
                </div>
              </div>

              {selectedTenant.status === 'active' && (
                <>
                  {/* Rental Info */}
                  <div className="border-t border-gray-200 pt-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Rental Information</h4>
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <label className="text-sm font-medium text-gray-700">Properties Rented</label>
                        <p className="text-lg font-semibold text-gray-900">{selectedTenant.propertiesRented}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700">Total Monthly Rent</label>
                        <p className="text-lg font-semibold text-gray-900">SAR {selectedTenant.totalMonthlyRent.toLocaleString()}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700">Active Contracts</label>
                        <p className="text-lg text-gray-900">{selectedTenant.activeContracts}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700">Total Paid</label>
                        <p className="text-lg text-green-600">SAR {selectedTenant.totalPaid.toLocaleString()}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700">Total Pending</label>
                        <p className={`text-lg font-semibold ${selectedTenant.totalPending > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                          SAR {selectedTenant.totalPending.toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700">Payment History</label>
                        <p className={`text-lg font-semibold capitalize ${getPaymentHistoryColor(selectedTenant.paymentHistory)}`}>
                          {selectedTenant.paymentHistory}
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {selectedTenant.notes && (
                <div className="border-t border-gray-200 pt-6">
                  <label className="text-sm font-medium text-gray-700">Notes</label>
                  <p className="text-lg text-gray-900 mt-2">{selectedTenant.notes}</p>
                </div>
              )}

              <div className="border-t border-gray-200 pt-6 flex gap-3 flex-wrap">
                <button 
                  onClick={() => {
                    setEditingTenant(selectedTenant);
                    setSelectedTenant(null);
                  }}
                  className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 flex items-center gap-2"
                >
                  <Edit className="w-4 h-4" />
                  Edit Tenant
                </button>
                <button 
                  onClick={() => {
                    setSelectedTenant(null);
                    // Navigate to rentals tab would be handled by parent
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
                >
                  <FileText className="w-4 h-4" />
                  View Contracts
                </button>
                <button 
                  onClick={() => {
                    setSelectedTenant(null);
                    // Navigate to invoices tab would be handled by parent
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
                >
                  <DollarSign className="w-4 h-4" />
                  View Invoices
                </button>
                <button 
                  onClick={() => handleDeleteTenant(selectedTenant.id)}
                  className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 flex items-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Tenant Form Modal */}
      {(showCreateModal || editingTenant) && (
        <TenantForm
          tenant={editingTenant}
          onSave={handleSaveTenant}
          onClose={() => {
            setShowCreateModal(false);
            setEditingTenant(null);
          }}
        />
      )}
    </div>
  );
};

export default Tenants;
